// TaskManagerCommentModal.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IUserRef } from "../../models/userData.model";
import Button, { ButtonType } from "../../assets/Button";
import useOutsideClick from "../Assets/useOutsideClick";
import MentionEditor from "../Assets/TiptapMentionEditor";

interface TaskManagerCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: IUserRef[]; // original shape: { _id, username }
  handleAddComment: (mentions: string[], comment: string) => void;
}

const TaskManagerCommentModal: React.FC<TaskManagerCommentModalProps> = ({
  isOpen,
  onClose,
  users,
  handleAddComment,
}) => {
  // tiptap editor instance
  const editorRef = useRef<any | null>(null);
  const editorContainerRef = useRef<HTMLDivElement | null>(null);

  // mentions meta & html captured from MentionEditor
  const [mentionsMeta, setMentionsMeta] = useState<
    { id: string; label: string }[]
  >([]);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [commentHtml, setCommentHtml] = useState<string>("");

  // map your user objects to tiptap-friendly items { id, label, avatar? }
  const tiptapUsers = users.map((u) => ({
    id: u._id,
    label: u.username,
    avatar: null,
  }));

  // reset on close
  useEffect(() => {
    if (!isOpen) {
      setMentionsMeta([]);
      setMentionIds([]);
      setCommentHtml("");
      if (editorRef.current) {
        // clear content silently
        editorRef.current.commands.clearContent();
      }
    } else {
      setTimeout(() => editorRef.current?.commands.focus?.(), 80);
    }
  }, [isOpen]);

  const handleEditorChange = useCallback(
    (ids: string[], html: string, meta?: { id: string; label: string }[]) => {
      setMentionIds(ids);
      setCommentHtml(html);
      setMentionsMeta(meta || []);
    },
    []
  );

  const handleGetEditor = (editor: any | null) => {
    editorRef.current = editor;
  };

  // remove ALL occurrences of a mention id (silent)
  const removeAllOccurrences = (id: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    const { state } = editor.view;
    let tr = state.tr;
    const positions: number[] = [];

    // collect positions of mention nodes matching the id
    state.doc.descendants((node: any, pos: number) => {
      if (node.type.name === "mention" && node.attrs && node.attrs.id === id) {
        positions.push(pos);
      }
    });

    if (positions.length === 0) {
      // optimistic update of meta
      setMentionsMeta((prev) => prev.filter((x) => x.id !== id));
      setMentionIds((prev) => prev.filter((x) => x !== id));
      return;
    }

    // delete from end to start to avoid shifting positions
    positions.sort((a, b) => b - a);
    positions.forEach((pos) => {
      const node = state.doc.nodeAt(pos);
      if (!node) return;
      tr = tr.delete(pos, pos + node.nodeSize);
      // insert a space so words don't join
      tr = tr.insert(pos, state.schema.text(" "));
    });

    if (tr.docChanged) {
      editor.view.dispatch(tr);
      editor.commands.focus();

      // recompute mentionsMeta and ids from new doc
      const html = editor.getHTML();
      const json = editor.getJSON();
      const mentionIdsSet = new Set<string>();
      const mentionsMap = new Map<string, string>();
      const walk = (node: any) => {
        if (!node) return;
        if (node.type === "mention" && node.attrs && node.attrs.id) {
          mentionIdsSet.add(node.attrs.id);
          mentionsMap.set(node.attrs.id, node.attrs.label || node.attrs.id);
        }
        if (node.content && Array.isArray(node.content)) {
          node.content.forEach(walk);
        }
      };
      walk(json);

      setMentionIds(Array.from(mentionIdsSet));
      setMentionsMeta(
        Array.from(mentionsMap.entries()).map(([id, label]) => ({ id, label }))
      );
      setCommentHtml(html);
    }
  };

  const handleSubmit = () => {
    if (!commentHtml || !commentHtml.trim()) return;
    handleAddComment(mentionIds, commentHtml);
    // reset
    setMentionsMeta([]);
    setMentionIds([]);
    setCommentHtml("");
    if (editorRef.current) editorRef.current.commands.clearContent?.();
    onClose();
  };

  // click outside behavior (TipTap suggestion handles itself but we blur)
  useOutsideClick(editorContainerRef, () => {
    editorRef.current?.commands.blur?.();
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <h2 className="text-sm font-semibold mb-4">Add Comment</h2>
        <div
          className="relative border border-gray-100 p-2 rounded-lg"
          ref={editorContainerRef}
        >
          <MentionEditor
            users={tiptapUsers}
            initialHtml={commentHtml}
            onChange={handleEditorChange}
            getEditor={handleGetEditor}
            placeholder="Type your comment. Use @ to mention someone..."
          />
        </div>

        <div className="mb-3 mt-3">
          <label className="block text-xs font-medium mb-1">Mentioned</label>
          <div className="flex gap-2 flex-wrap">
            {mentionsMeta.map((m) => (
              <div
                key={m.id}
                className="text-xs px-2 py-1 bg-blue-100 border border-blue-300 rounded-full flex items-center gap-2"
              >
                <span className="font-medium">@{m.label}</span>
                <button
                  onClick={() => {
                    removeAllOccurrences(m.id);
                    setMentionsMeta((prev) =>
                      prev.filter((x) => x.id !== m.id)
                    );
                    setMentionIds((prev) => prev.filter((x) => x !== m.id));
                  }}
                  className="text-red-500 text-xs"
                  type="button"
                >
                  ✕
                </button>
              </div>
            ))}
            {mentionsMeta.length === 0 && (
              <div className="text-xs text-gray-400">No one mentioned yet</div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            type={ButtonType.Cancel}
            onClick={onClose}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            type={ButtonType.Submit}
            onClick={handleSubmit}
            className="text-xs"
          >
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TaskManagerCommentModal;
